import React, { useEffect, useState } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import { Action, PartActionType } from '../../../../../@types/partAction.type';
import { PartLocationType } from '../../../../../@types/part.type';
import { PartTypeActionLiveSearchItem, PartTypeActionLiveSearchItemCategoryCode, PartOrPartTypeSearchData } from '../../../../../@types/partType.type';
import { useSearchPartsOrPartTypes } from '../../../../../queries/live-search-query';

export interface PartActionSearchFieldProps {
  label: string;
  clientContractIds: number[];
  locationId: number;
  actionType: string;
  /** Whether this field is searching for the IN (installed) or OUT (removed) part. */
  action: Action;
  onSelect: (item: PartTypeActionLiveSearchItem | null) => void;
}

/** Replace/Remove/DOA require serialised parts when searching for the OUT part. */
const SERIALISED_OUT_ACTIONS = new Set([PartActionType.REPLACE, PartActionType.REMOVE, PartActionType.DOA]);

export const PartActionSearchField: React.FC<PartActionSearchFieldProps> = ({
  label,
  clientContractIds,
  locationId,
  actionType,
  action,
  onSelect,
}) => {
  const searchMutation = useSearchPartsOrPartTypes();
  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState<PartTypeActionLiveSearchItem[]>([]);

  useEffect(() => {
    if (inputValue.length < 2) { setOptions([]); return; }
    const timer = setTimeout(() => {
      const isOutSerialised = action === Action.OUT && SERIALISED_OUT_ACTIONS.has(actionType as PartActionType);
      const isInAction = actionType === PartActionType.INSTALL || action === Action.IN;

      const searchData: PartOrPartTypeSearchData = {
        name: inputValue,
        serialiseType: isOutSerialised ? PartTypeActionLiveSearchItemCategoryCode.Serialised : '',
        clientContractIds,
        locationId: locationId ? String(locationId) : '',
        allowedPartLocationType: isInAction ? PartLocationType.Node : '',
        partActionType: actionType,
        searchType: 'part-and-part-type',
      };
      searchMutation.mutateAsync(searchData).then(setOptions).catch(() => setOptions([]));
    }, 300);
    return () => clearTimeout(timer);
  }, [inputValue]);

  return (
    <Box>
      <Typography sx={{ fontSize: '11px', fontWeight: 600, color: 'text.secondary', mb: 0.5 }}>
        {label}
      </Typography>
      <Autocomplete
        size="small"
        options={options}
        getOptionLabel={(opt) => opt.serial1 ? `${opt.partTypeName} - ${opt.serial1}` : opt.partTypeName || opt.name}
        filterOptions={(x) => x}
        inputValue={inputValue}
        onInputChange={(_, val) => setInputValue(val)}
        onChange={(_, item) => onSelect(item)}
        loading={searchMutation.isPending}
        renderInput={(params) => (
          <TextField {...params} placeholder="Search part type or serial..."
            sx={{ '& .MuiOutlinedInput-root': { fontSize: '12px' } }}
          />
        )}
        renderOption={(props, option) => (
          <li {...props} key={`${option.partTypeId}-${option.partId ?? option.ns_partId ?? option.name}`}>
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Typography sx={{ fontSize: '12px', fontWeight: 500 }}>
                {option.partTypeName || option.name}
                {option.serial1 && <Typography component="span" sx={{ fontSize: '11px', color: 'text.secondary', ml: 0.5 }}>({option.serial1})</Typography>}
              </Typography>
              <Typography sx={{ fontSize: '10px', color: 'text.secondary' }}>
                {option.categoryCode} &middot; {option.conditionCode || '—'} &middot; {option.locationName || '—'}
              </Typography>
            </Box>
          </li>
        )}
      />
    </Box>
  );
};
